import { useEffect, useState } from 'react';
import { AUDIT_ACTIONS, AUDIT_ENTITIES } from '@cannery/shared';
import { useAuth } from '../../auth/AuthContext';
import { api, ApiError } from '../../lib/api';
import { Badge, Button, Card, ErrorBanner, Pager, Spinner, inputCls } from '../../components/ui';
import { Icon } from '../../components/Icon';

/**
 * Audit Log viewer (System Settings → Audit Log).
 *
 * Reads the append-only trail through the existing `GET /api/audit` endpoint:
 * branch scope follows the admin branch switcher, action/record-type filters
 * and limit/offset pagination come from the server, and `total` is the server
 * count (never just the loaded rows). Authorization, branch isolation and
 * audit-write rules are enforced by the API - this component only renders
 * what the server returns.
 */

interface AuditEntryDto {
  id: string;
  createdAt: string;
  actorId: string | null;
  actorName: string | null;
  actorRole: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  branch: { code: string } | null;
  metadata: unknown;
  ip: string | null;
}

const PAGE_SIZE = 50;

/** `transaction.status_changed` -> `transaction status changed`. */
function humanize(code: string): string {
  return code.replace(/[._]/g, ' ');
}

const ACTION_TONES: Record<
  string,
  'slate' | 'indigo' | 'emerald' | 'amber' | 'rose'
> = {
  auth: 'slate',
  session: 'slate',
  branch: 'slate',
  user: 'indigo',
  transaction: 'emerald',
  service: 'emerald',
  delivery: 'emerald',
  inventory: 'amber',
  purchase: 'amber',
  expense: 'amber',
  cash_reconciliation: 'indigo',
  handover: 'indigo',
  control_review: 'rose',
};

function toneFor(action: string): 'slate' | 'indigo' | 'emerald' | 'amber' | 'rose' {
  return ACTION_TONES[action.split('.')[0] ?? ''] ?? 'slate';
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' });
}

export function AuditSection() {
  const { branchFilter, capabilities } = useAuth();
  const canRead = capabilities.includes('audit.read');

  const [entries, setEntries] = useState<AuditEntryDto[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // A new filter means a new result set: always start from the first page.
  useEffect(() => {
    setOffset(0);
  }, [branchFilter, action, entityType]);

  useEffect(() => {
    if (!canRead) return;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams();
    if (branchFilter !== 'ALL') params.set('branch', branchFilter);
    if (action) params.set('action', action);
    if (entityType) params.set('entityType', entityType);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(offset));

    void api
      .get<{ total: number; items: AuditEntryDto[] }>(`/audit?${params.toString()}`)
      .then((res) => {
        if (!cancelled) {
          setEntries(res.items);
          setTotal(res.total);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Failed to load the audit trail.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canRead, branchFilter, action, entityType, offset, refreshKey]);

  if (!canRead) return null;

  return (
    <section id="audit" aria-label="Audit trail" className="scroll-mt-24 space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900">Audit trail</h2>
          <p className="mt-1 text-sm text-slate-500">
            Append-only record of WHO did WHAT, WHEN and in WHICH branch. Scope follows the branch
            switcher; entries can never be edited or deleted.
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setRefreshKey((k) => k + 1)}
          disabled={loading}
        >
          <Icon name="refresh" className="h-5 w-5" />
          Refresh
        </Button>
      </div>

      <Card className="flex flex-wrap items-center gap-3 p-4">
        <select
          aria-label="Filter audit log by action"
          className={`${inputCls} h-11 w-auto`}
          value={action}
          onChange={(e) => setAction(e.target.value)}
        >
          <option value="">All actions</option>
          {[...Object.values(AUDIT_ACTIONS)].sort().map((a) => (
            <option key={a} value={a}>
              {humanize(a)}
            </option>
          ))}
        </select>
        <select
          aria-label="Filter audit log by record type"
          className={`${inputCls} h-11 w-auto`}
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
        >
          <option value="">All record types</option>
          {[...Object.values(AUDIT_ENTITIES)].sort().map((t) => (
            <option key={t} value={t}>
              {humanize(t)}
            </option>
          ))}
        </select>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-10">
            <Spinner className="h-6 w-6 text-indigo-600" />
          </div>
        ) : total === 0 ? (
          <div className="py-10 text-center">
            <p className="text-sm font-medium text-slate-700">No audit entries match.</p>
            <p className="mt-1 text-xs text-slate-500">
              Logins, account changes and record edits appear here as soon as they happen.
            </p>
          </div>
        ) : (
          <>
            <ul className="divide-y divide-slate-100 text-sm">
              {entries.map((entry) => (
                <li key={entry.id}>
                  <button
                    type="button"
                    aria-expanded={expandedId === entry.id}
                    onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                    className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-500"
                  >
                    <span className="whitespace-nowrap tabular-nums text-slate-500">
                      {formatWhen(entry.createdAt)}
                    </span>
                    <span className="font-medium text-slate-800">{entry.actorName ?? 'system'}</span>
                    {entry.actorRole && <Badge tone="indigo">{entry.actorRole}</Badge>}
                    <Badge tone={toneFor(entry.action)}>{humanize(entry.action)}</Badge>
                    <span className="text-slate-500">{humanize(entry.entityType)}</span>
                    {entry.branch && <Badge tone="slate">{entry.branch.code}</Badge>}
                    <span className="ml-auto text-xs text-slate-400">{entry.ip ?? 'no ip'}</span>
                  </button>
                  {expandedId === entry.id && (
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-3 text-xs">
                      <dl className="grid grid-cols-2 gap-2">
                        <div>
                          <dt className="font-semibold text-slate-600">Record</dt>
                          <dd className="font-mono text-slate-700">
                            {entry.entityType}
                            {entry.entityId ? ` · ${entry.entityId}` : ''}
                          </dd>
                        </div>
                        <div>
                          <dt className="font-semibold text-slate-600">Actor</dt>
                          <dd className="text-slate-700">
                            {entry.actorName ?? '—'} ({entry.actorRole ?? '—'}) ·{' '}
                            {entry.ip ?? 'no ip recorded'}
                          </dd>
                        </div>
                      </dl>
                      <pre className="mt-2 overflow-x-auto rounded-lg bg-panel p-2 font-mono text-[11px] leading-relaxed text-slate-700 ring-1 ring-slate-200">
                        {JSON.stringify(entry.metadata ?? {}, null, 2)}
                      </pre>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <Pager
              total={total}
              offset={offset}
              loaded={entries.length}
              onPrev={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              onNext={() => setOffset(offset + PAGE_SIZE)}
            />
          </>
        )}
      </Card>
    </section>
  );
}
